/**
 * Tatbikat kural parametreleri (ADR 0128 §4): ürünün varsayılan seti `DRILL-RULES-1.0`. Bunlar
 * **varsayılan parametredir**; kesin doğrulama yetkili mevzuat sorumlusundadır (ortağın belgesi
 * §4). Faz 2'de şirket kendi sürümlü kural setiyle değiştirir; motor (Next Due) Faz 2'dedir.
 *
 * Paragraf numaraları SOLAS III/19'un MSC.350(92) (2015) değişikliğiyle kaydı; kaynaklar iki
 * numarayı da kullanır, eski numara parantezde. Okunan kaynaklar ADR 0128 "Emsaller ve kaynaklar".
 */

export const DRILL_RULES_VERSION = 'DRILL-RULES-1.0';

/** `regulation`: mevzuat sıklığı; `company_sms`: mevzuatta sıklık yok, şirket SMS'i belirler. */
export const DRILL_RULE_BASES = ['regulation', 'company_sms'] as const;
export type DrillRuleBasis = (typeof DRILL_RULE_BASES)[number];

/** Kuralın uygulandığı gemi: bütün gemiler ya da yalnız yolcu gemisi. */
export const DRILL_RULE_SCOPES = ['all', 'passenger'] as const;
export type DrillRuleScope = (typeof DRILL_RULE_SCOPES)[number];

export interface DrillRule {
  code: string;
  title: string;
  reference: string;
  basis: DrillRuleBasis;
  scope: DrillRuleScope;
  /** Aralık (ay); tetikli kuralda `null`. */
  intervalMonths: number | null;
  /** Tetikli kural: olaydan sonra en geç (saat). */
  triggerHours: number | null;
  note: string;
}

export const DEFAULT_DRILL_RULES: readonly DrillRule[] = [
  {
    code: 'SOLAS-FIRE',
    title: 'Fire drill — every crew member',
    // [SOLAS III/19.3.2] her mürettebat ayda en az bir yangın tatbikatı
    reference: 'SOLAS III/19.3.2',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 1,
    triggerHours: null,
    note: 'Every crew member takes part in at least one fire drill every month.',
  },
  {
    code: 'SOLAS-ABANDON',
    title: 'Abandon ship drill — every crew member',
    // [SOLAS III/19.3.2]
    reference: 'SOLAS III/19.3.2',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 1,
    triggerHours: null,
    note: 'Every crew member takes part in at least one abandon ship drill every month.',
  },
  {
    code: 'SOLAS-CREW-CHANGE',
    title: 'Fire and abandon ship drills after crew change',
    // [SOLAS III/19.3.2] %25'ten fazlası önceki ay o gemide katılmadıysa kalkıştan sonra 24 saat
    reference: 'SOLAS III/19.3.2',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: null,
    triggerHours: 24,
    note: 'Within 24 hours of leaving port if more than 25% of the crew have not taken part in abandon ship and fire drills on board that ship in the previous month.',
  },
  {
    code: 'SOLAS-LIFEBOAT-LAUNCH',
    title: 'Lifeboat launched and manoeuvred in the water',
    // [SOLAS III/19.3.4.3] (eski 19.3.3.3) üç ayda bir, gemiyi terk tatbikatında
    reference: 'SOLAS III/19.3.4.3 (formerly 19.3.3.3)',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 3,
    triggerHours: null,
    note: 'Each lifeboat launched with its assigned operating crew and manoeuvred in the water at least once every three months during an abandon ship drill.',
  },
  {
    code: 'SOLAS-FREEFALL-LAUNCH',
    title: 'Free-fall lifeboat launching',
    // [SOLAS III/19.3.4.4] (eski 19.3.3.4) altı ayda bir; uygulanamıyorsa indirme ya da benzetim
    reference: 'SOLAS III/19.3.4.4 (formerly 19.3.3.4)',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 6,
    triggerHours: null,
    note: 'Free-fall launching with operating crew, or simulated launching, at least every six months.',
  },
  {
    code: 'SOLAS-RESCUE-BOAT',
    title: 'Rescue boat launched and manoeuvred',
    // [SOLAS III/19.3] kurtarma botu makul ve uygulanabilir ölçüde her ay, en az üç ayda bir
    reference: 'SOLAS III/19.3 (rescue boats)',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 3,
    triggerHours: null,
    note: 'Launched each month where reasonable and practicable; in all cases at least once every three months.',
  },
  {
    code: 'SOLAS-ENCLOSED-SPACE',
    title: 'Enclosed space entry and rescue drill',
    // [SOLAS III/19.3.3, 19.3.6] MSC.350(92), 1 Ocak 2015: iki ayda bir
    reference: 'SOLAS III/19.3.3, III/19.3.6',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 2,
    triggerHours: null,
    note: 'Crew members with enclosed space entry or rescue responsibilities take part at least once every two months.',
  },
  {
    code: 'SOLAS-EMERGENCY-STEERING',
    title: 'Emergency steering drill',
    // [SOLAS V/26.4] üç ayda bir
    reference: 'SOLAS V/26.4',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 3,
    triggerHours: null,
    note: 'At least once every three months, including direct control from the steering gear compartment.',
  },
  {
    code: 'SOLAS-DAMAGE-CONTROL',
    title: 'Damage control drill (passenger ships)',
    // [SOLAS II-1/19-1] yalnız yolcu gemisi, 1 Ocak 2020; yük gemisinde şirket SMS'i
    reference: 'SOLAS II-1/19-1',
    basis: 'regulation',
    scope: 'passenger',
    intervalMonths: 3,
    triggerHours: null,
    note: 'Passenger ships only. Cargo ships: company SMS sets the interval (ISM-PROGRAM).',
  },
  {
    code: 'ISPS-DRILL',
    title: 'Ship security drill',
    // [ISPS B/13.6] üç ayda bir; bayrak bağlayıcı yapabilir
    reference: 'ISPS Code B/13.6',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: 3,
    triggerHours: null,
    note: 'At least once every three months. Exercise once each calendar year, not more than 18 months apart (B/13.7).',
  },
  {
    code: 'ISPS-CREW-CHANGE',
    title: 'Security drill after crew change',
    // [ISPS B/13.6] %25'ten fazla değişimde bir hafta içinde
    reference: 'ISPS Code B/13.6',
    basis: 'regulation',
    scope: 'all',
    intervalMonths: null,
    triggerHours: 168,
    note: 'Within one week if more than 25% of the crew changed with persons who have not taken part in a drill on that ship in the last three months.',
  },
  {
    code: 'MARPOL-SOPEP',
    title: 'Oil pollution (SOPEP) drill',
    // [MARPOL Ek I K.37] plan zorunlu; sıklık mevzuatta yok, şirket SMS varsayılanı üç ay
    reference: 'MARPOL Annex I reg. 37 (plan); interval: company SMS',
    basis: 'company_sms',
    scope: 'all',
    intervalMonths: 3,
    triggerHours: null,
    note: 'MARPOL requires the SOPEP; the drill interval is a company SMS default, not a regulation.',
  },
  {
    code: 'ISM-PROGRAM',
    title: 'Company emergency drill programme',
    // [ISM 8.2] şirket tatbikat programı kurar; sıklık şirketin
    reference: 'ISM Code 8.2',
    basis: 'company_sms',
    scope: 'all',
    intervalMonths: 3,
    triggerHours: null,
    note: 'Other shipboard emergencies; the company sets the interval in its SMS.',
  },
];

export const DRILL_RULE_CODES: readonly string[] = DEFAULT_DRILL_RULES.map((r) => r.code);

export function drillRule(code: string | null): DrillRule | null {
  return DEFAULT_DRILL_RULES.find((r) => r.code === code) ?? null;
}
